import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import GlobalConnectionBanner from './components/ui/GlobalConnectionBanner';
import PageLoader from './components/ui/PageLoader';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ProjectsPage = lazy(() => import('./pages/workspace/ProjectsPage'));
const TeamsPage = lazy(() => import('./pages/workspace/TeamsPage'));
const ContentCreationPage = lazy(() => import('./pages/workspace/ContentCreationPage'));
const QuantificationPage = lazy(() => import('./pages/workspace/QuantificationPage'));
const UseCasesPage = lazy(() => import('./pages/workspace/UseCasesPage'));
const FreeAssistedPage = lazy(() => import('./pages/workspace/FreeAssistedPage'));
const DocumentsLibrePage = lazy(() => import('./pages/workspace/DocumentsLibrePage'));
const AIVisualGeneratorPage = lazy(() => import('./pages/workspace/AIVisualGeneratorPage'));
const AdminPage = lazy(() => import('./pages/workspace/AdminPage'));
const NotificationsPage = lazy(() => import('./pages/workspace/NotificationsPage'));
const SettingsPage = lazy(() => import('./pages/workspace/SettingsPage'));
const DocumentManagementPage = lazy(() => import('./pages/DocumentManagementPage'));
const StrategicWritingPage = lazy(() => import('./pages/StrategicWritingPage'));
const EvaluationTrackingPage = lazy(() => import('./pages/EvaluationTrackingPage'));
const CommunicationCreationPage = lazy(() => import('./pages/CommunicationCreationPage'));

function App() {
  useEffect(() => {
    const assets = ['/logo.png', '/sidebar.png', '/Gavi-logo_1b.png', '/logo-MSHP.png', '/logo-eg.png'];
    assets.forEach((src) => {
      const image = new Image();
      image.src = src;
    });
  }, []);

  return (
    <Router>
      <div className="App">
        <GlobalConnectionBanner />
        <ScrollToTop />
        <Suspense
          fallback={<PageLoader />}
        >
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            {/* Layout route partagé — DashboardLayout (sidebar + header + footer) monté une seule fois */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/workspace" element={<Navigate to="/workspace/documents-libre" replace />} />
              <Route path="/workspace/documents-libre" element={<DocumentsLibrePage />} />
              <Route path="/workspace/redaction-libre" element={<FreeAssistedPage />} />
              <Route path="/workspace/content" element={<ContentCreationPage />} />
              <Route path="/workspace/quantification" element={<QuantificationPage />} />
              <Route path="/workspace/ai-visual-generator" element={<AIVisualGeneratorPage />} />
              <Route path="/workspace/usecases" element={<UseCasesPage />} />
              <Route path="/workspace/notifications" element={<NotificationsPage />} />
              <Route path="/workspace/profile" element={<SettingsPage />} />
              <Route path="/workspace/admin" element={<AdminPage />} />
<Route path="/workspace/settings" element={
                <ProtectedRoute requiredCapabilities={['settings.view']}>
                  <SettingsPage />
                </ProtectedRoute>
              } />
              <Route path="/workspace/projects" element={
                <ProtectedRoute requiredCapabilities={['projects.view']}>
                  <ProjectsPage />
                </ProtectedRoute>
              } />
              <Route path="/workspace/teams" element={
                <ProtectedRoute requiredCapabilities={['teams.view']}>
                  <TeamsPage />
                </ProtectedRoute>
              } />
            </Route>
            {/* Routes publiques pour les modules */}
            <Route path="/document-management" element={<DocumentManagementPage />} />
            <Route path="/strategic-writing" element={<StrategicWritingPage />} />
            <Route path="/evaluation-tracking" element={<EvaluationTrackingPage />} />
            <Route path="/communication-creation" element={<CommunicationCreationPage />} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;

const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
};
